import { useCallback, useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BiSolidLockOpen, BiSolidRocket, BiX } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import {
  useLazyGetPlanInfoQuery,
  useValidatePromotionCodeMutation
} from "../../app/services/billing-rtk.service";
import { ChatbotRTKService } from "../../app/services/chatbot-rtk.services";
import { MultiLanguageUrl } from "../../constants/multiLanguage.constant";
import { handleToggleAppearanceModal } from "../../features/appearance/appearanceSlice";
import { selectUseId } from "../../features/auth/auth.slice";
import { handleToggleWizardModal } from "../../features/bot/botSlice";
import { handleToggleShowUpgradePlanModal } from "../../features/ui/uiSlice";
import DashboardLinkTo from "../common/DashboardLinkTo";

const UpgradeModal = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUseId);
  const [getPlanInfo] = useLazyGetPlanInfoQuery();

  const showUpgradePlanModal = useAppSelector(
    (state) => state.ui.showUpgradePlanModal
  );

  const isBotModalOpen = useAppSelector((state) => state.bots.wizard.showModal);
  const isAppearanceModalOpen = useAppSelector(
    (state) => state.appearance.showModal
  );
  const [ignorePromo, setIgnorePromo] = useState(false);

  const shouldShowPromoButton = useCallback(async () => {
    if (!showUpgradePlanModal) return;

    const shouldIgnorePromoCode = localStorage.getItem(
      `louise_ignore_promo_${userId}`
    );
    if (shouldIgnorePromoCode) {
      setIgnorePromo(true);
      return;
    }

    try {
      const result = await getPlanInfo();
      const hasPromoCode = result?.data?.some(
        (x) => x.name === "UNLOCK_ALL_WELCOME" || x.name.includes("LB_APPSUMO")
      );

      if (hasPromoCode) {
        localStorage.setItem(`louise_ignore_promo_${userId}`, "true");
        setIgnorePromo(true);
      } else {
        setIgnorePromo(false);
      }
    } catch (error) {
      console.error("Error fetching plan info:", error);
    }
  }, [showUpgradePlanModal, userId]);

  useEffect(() => {
    shouldShowPromoButton();
  }, [shouldShowPromoButton]);

  const [validatePromotionCode, ValidatePromotionCodeStatus] =
    useValidatePromotionCodeMutation();

  const sendToRewardPage = () => {
    handleCloseModal();
    navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/rewards`);
  };

  const checkPromoCode = () => {
    const promoCode = "UNLOCK_ALL_WELCOME";

    validatePromotionCode({ code: promoCode })
      .unwrap()
      .then(() => {
        dispatch(
          ChatbotRTKService.util.invalidateTags(["Package", "GetAllScenarios"])
        );

        toast.success(t("BillingCreatePage/l-04"));

        dispatch(handleToggleShowUpgradePlanModal(false));

        navigate(
          `/${
            MultiLanguageUrl[i18n.language]
          }/verify-plan?plan_name=${promoCode}&status=true`
        );
      })
      .catch((res: any) => {
        const codeMessage =
          res?.data?.code && t(`BillingCreatePage/${res.data.code}`);
        const defaultMessage = t("BillingCreatePage/l-03");

        if (codeMessage) {
          toast.warning(codeMessage);
        } else if (res?.data?.message) {
          toast.warning(res.data.message);
        } else {
          toast.warning(defaultMessage);
        }
      });
  };

  const handleCloseModal = () => {
    if (isAppearanceModalOpen) dispatch(handleToggleAppearanceModal(false));

    if (isBotModalOpen) dispatch(handleToggleWizardModal({ status: false }));

    dispatch(handleToggleShowUpgradePlanModal(false));
  };

  const closeModal = () => {
    dispatch(handleToggleShowUpgradePlanModal(false));
  };

  return (
    <Modal
      size={"xl"}
      show={showUpgradePlanModal}
      centered
      onHide={closeModal}
      className="tw-bg-gray-500/75 tw-backdrop-blur-sm"
      dialogClassName=" xl:tw-w-[850px] tw-w-full tw-m-0 sm:tw-m-auto"
      contentClassName="xl:tw-p-0 xl:tw-rounded-full  tw-rounded-none "
    >
      <div className="tw-flex xl:tw-flex-row tw-flex-col">
        <div className="tw-flex-1 tw-px-[52px] tw-py-8 tw-bg-gray-50 tw-rounded-tl-2xl tw-rounded-bl-2xl tw-flex-col tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
          <BiSolidRocket className="tw-w-[164px] tw-h-[164px]" />
        </div>

        <div className="tw-px-[20px] xl:tw-w-[632px] tw-w-full xl:tw-pl-[52px]  xl:tw-pr-3 tw-py-3 tw-bg-white tw-rounded-tr-2xl tw-rounded-br-2xl tw-justify-start tw-items-start tw-gap-8 tw-inline-flex">
          <div className="tw-grow tw-shrink tw-basis-0 tw-py-5 tw-flex-col tw-justify-start tw-items-start tw-gap-[52px] tw-inline-flex">
            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
              <div className="tw-self-stretch tw-h-full tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
                <div className="tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
                  <div className="tw-text-gray-700 tw-text-3xl tw-font-semibold tw-font-['Lexend'] tw-leading-9">
                    {t("DashboardUpgrade/title")}
                  </div>
                  <div className="tw-w-5 tw-h-5 tw-p-0.5 tw-origin-top-left tw-rotate-180 tw-opacity-0 tw-justify-center tw-items-center tw-flex" />
                </div>
                <div className="tw-self-stretch tw-text-gray-500 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                  {t("DashboardUpgrade/description")}
                </div>
              </div>
              <DashboardLinkTo
                to="billing-create"
                onClick={handleCloseModal}
                className="tw-cursor-pointer tw-pl-[11px] tw-pr-[13px] tw-py-[7px] tw-bg-primary tw-rounded-[99px] tw-shadow tw-justify-center tw-items-center tw-gap-2 tw-inline-flex"
              >
                <BiSolidRocket className="tw-text-white tw-w-4 tw-h-4 tw-relative" />
                <div className="tw-text-white hover:tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t("DashboardUpgrade/btn")}
                </div>
              </DashboardLinkTo>
            </div>

            {!ignorePromo ? (
              <div className="tw-w-full tw-p-6 tw-bg-gray-50 tw-rounded-2xl tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-3 tw-inline-flex">
                <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex">
                  <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-3 tw-inline-flex">
                    <div className="tw-grow tw-shrink tw-basis-0 tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                      {t("DashboardUpgrade/l-01")}
                    </div>
                  </div>
                  <button
                    disabled={ValidatePromotionCodeStatus.isLoading}
                    onClick={checkPromoCode}
                    className="tw-pl-[11px] tw-pr-[13px] tw-py-[9px] tw-bg-white tw-rounded-[99px] tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-inline-flex"
                    type="button"
                  >
                    <span className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {"🎁 "}
                      {t("DashboardUpgrade/btn-01")}
                      {ValidatePromotionCodeStatus.isLoading && (
                        <Spinner size="sm" />
                      )}
                    </span>
                  </button>
                </div>
                <div className="tw-w-12 tw-h-12 tw-relative tw-text-gray-500">
                  <BiSolidLockOpen size={40} />
                </div>
              </div>
            ) : (
              <div className="tw-w-full tw-p-6 tw-bg-gray-50 tw-rounded-2xl tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-3 tw-inline-flex">
                <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex">
                  <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-3 tw-inline-flex">
                    <div className="tw-grow tw-shrink tw-basis-0 tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                      {t("DashboardUpgrade/l-02")}
                    </div>
                  </div>
                  <button
                    disabled={ValidatePromotionCodeStatus.isLoading}
                    onClick={sendToRewardPage}
                    className="tw-pl-[11px] tw-pr-[13px] tw-py-[9px] tw-bg-white tw-rounded-[99px] tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-inline-flex"
                    type="button"
                  >
                    <span className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {"🎁 "}
                      {t("DashboardUpgrade/btn-02")}
                      {ValidatePromotionCodeStatus.isLoading && (
                        <Spinner size="sm" />
                      )}
                    </span>
                  </button>
                </div>
                <div className="tw-w-12 tw-h-12 tw-relative tw-text-gray-500">
                  <BiSolidLockOpen size={40} />
                </div>
              </div>
            )}
          </div>
          <div
            onClick={closeModal}
            onKeyDown={closeModal}
            role="button"
            tabIndex={0}
            className="tw-cursor-pointer tw-w-6 tw-h-6 xl:tw-relative tw-absolute tw-top-2  tw-right-2"
          >
            <BiX className="tw-w-7 tw-h-7 tw-relative tw-text-neutral-400" />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UpgradeModal;
